import _ from 'underscore';

window._ = _;

function _recursiveLooper(method, hierarchy, iterator, context, config = {}) {
  let childrenNamespace;
  function process(item, key, items) {
    // if false - break the loop.
    if (iterator.call(context || _, item, key, items) === false) {
      return false;
    }
    return _[method](item[childrenNamespace] || [], process);
  }

  if (!hierarchy) {
    return [];
  }

  childrenNamespace = config.children || 'children';
  const checkCircular = config.checkCircular !== false;

  // check if hierarchy has no circular references
  if (checkCircular) {
    try {
      JSON.stringify(hierarchy);
    } catch (x) {
      throw new Error(x.message);
    }
  }

  if (!_.isArray(hierarchy)) {
    process(hierarchy);
  } else {
    _[method](hierarchy, process);
  }
  return hierarchy;
}

/**
 * creates a deep copy with all brand new memory references of the object and it's nesting
 * @param object source object
 * @returns {object} copy
 */
export function deepClone(object) {
  if (!_.isObject(object)) {
    return object;
  }
  return JSON.parse(JSON.stringify(object));
}

/**
 * Calls iterator function on each item in the hierarchy including the deepest children
 * @param {Object<children>|Array.<children>} hierarchy - a nested collection with params and it's children
 * @param {Function} iterator - a function to be called on each item
 * @param {Object} [context] - optional reference to an object context. Default: _
 * @param {Object} [config] - optional config.
 *  @prop {string} children - namespace of children property in the item hierarchy, Default: 'children'
 *  @prop {boolean} checkCircular - check if the hierarchy has no circular references where it's needed.
 * @returns {Object|Array} updated hierarchy
 */
// eslint-disable-next-line no-unused-vars
export function eachHierarchy(hierarchy, iterator, context, config) {
  // eslint-disable-next-line prefer-rest-params
  return _recursiveLooper('each', ...arguments);
}

/**
 * Calls iterator function on every item in the hierarchy including the deepest children, supports break
 * @param {Object<children>|Array.<children>} hierarchy - a nested collection with params and it's children
 * @param {Function} iterator - a function to be called on each item
 * @param {Object} context - optional reference to an object context. Default: _
 * @param {Object} config - optional config.
 *  @prop {string} children - namespace of children property in the item hierarchy, Default: 'children'
 *  @prop {boolean} checkCircular - check if the hierarchy has no circular references where it's needed.
 * @returns {Object|Array} updated hierarchy
 */
// eslint-disable-next-line no-unused-vars
export function everyHierarchy(hierarchy, iterator, context, config) {
  // eslint-disable-next-line prefer-rest-params
  return _recursiveLooper('every', ...arguments);
}

export function htmlDecode(str) {
  const a = document.createElement('a');
  a.innerHTML = str;
  return a.textContent;
}

/**
 * Get object property value by namespace.
 * @param {Object|Array} obj - object for searching
 * @param {String} namespace - property namespace
 * @param {Object} [defaults] - default value if property can't be found. Default: undefined
 * @returns {*} - property value
 */
export function getValueByNs(obj, namespace, defaults) {
  if (!obj) {
    return defaults; // by default "defaults" is undefined
  }
  if (!namespace) {
    return obj;
  }
  try {
    const keys = namespace.split('.'); // Currently doesn't support brackets. Use dot notation for array.
    // eslint-disable-next-line no-plusplus
    for (let i = 0, l = keys.length - 1; i <= l && obj; i++) {
      // eslint-disable-next-line no-param-reassign
      obj = obj[keys[i]];
    }
    // eslint-disable-next-line sonarjs/no-ignored-exceptions,no-unused-vars
  } catch (e) {
    // eslint-disable-next-line no-param-reassign,no-void
    obj = void 0;
  }
  if (_.isUndefined(obj)) {
    // Logger.debug('Possibly malformed data object consumed in namespace:', namespace, '. returning fallback:', defaults);
    return defaults;
  }
  return obj;
}

/**
 * Apply only the last call within the wait window
 * @param {Function} func - initial function
 * @param {Number} wait - milliseconds
 * @param {Object} scope - scope of the initial function
 * @returns {*} - property value
 */
export function buffer(func, wait, scope) {
  let timer = null;
  return () => {
    if (timer) clearTimeout(timer);
    // eslint-disable-next-line prefer-rest-params
    const args = arguments;
    timer = setTimeout(() => {
      timer = null;
      func.apply(scope, args);
    }, wait);
  };
}

export function keyify(obj, keyParts) {
  const picked = keyParts ? _.pick(obj, keyParts) : obj;
  return _.values(picked).join('/');
}

_.mixin({
  deepClone,
  eachHierarchy,
  everyHierarchy,
  htmlDecode,
  buffer,
  getValueByNs,
  keyify,
});
